import classnames from 'classnames';
import React from 'react';
import SEO from './components/SEO';
import styles from './PolicyPages.module.scss';

const Privacy = () => {
    return (
        <>
            <SEO
                title="AG Grid: Privacy Policy"
                description="We take your privacy very seriously at AG Grid. This page outlines our privacy policy which we have updated in light of GDPR."
            />
            <div className={classnames(styles.policyPage, 'ag-styles')}>
                <div className="page-margin">
                    <h1>AG Grid Privacy Policy</h1>
                    <hr />

                    <section>
                        <header>
                            <div className={styles.introduction} id="introduction">
                                <h4>Effective Date: 6 July 2022</h4>
                                <p>Welcome to AG Grid's Privacy Policy.</p>
                                <p>
                                    <strong>Your privacy is important to us.</strong>
                                </p>
                                <p>
                                    At ag-grid, we are fully committed to protecting your personal data and complying
                                    with all data privacy laws.{' '}
                                </p>
                                <p>
                                    This policy serves as a guide and reference point for how we may collect and use
                                    personal information, and the rights and choices available to all our visitors and
                                    customers.
                                </p>
                                <p>
                                    We strongly recommend you read our policy and understand what we collect, how we
                                    collect it, what we do with it, how we protect it, and your rights regarding
                                    information, <strong>before</strong> you use or access any of our services.
                                </p>
                            </div>
                            <nav>
                                <ul className="list-style-none">
                                    <li>
                                        <a href="# active">Guide</a>
                                    </li>
                                    <li>
                                        <a href="#intro-privacy">Introduction</a>
                                    </li>
                                    <li>
                                        <a href="#data-what-why">
                                            What Do We Collect And For What Purpose And Ground We Process It
                                        </a>
                                    </li>
                                    <li>
                                        <a href="#data-how-we-collect">How We Collect Your Personal Data</a>
                                    </li>
                                    <li>
                                        <a href="#marketing-comms">Marketing Communications</a>
                                    </li>
                                    <li>
                                        <a href="#data-disclosure">Disclosures Of Your Personal Data</a>
                                    </li>
                                    <li>
                                        <a href="#intl-transfers">International Transfers</a>
                                    </li>
                                    <li>
                                        <a href="#data-security">Data Security</a>
                                    </li>
                                    <li>
                                        <a href="#data-retention">Data Retention</a>
                                    </li>
                                    <li>
                                        <a href="#legal-rights">Your Legal Rights</a>
                                    </li>
                                    <li>
                                        <a href="#third-party-links">Third-Party Links</a>
                                    </li>
                                    <li>
                                        <a href="#cookies-privacy">Cookies</a>
                                    </li>
                                </ul>
                            </nav>
                        </header>
                        <ol className={styles.policyList}>
                            <li>
                                <h3 id="intro-privacy">Introduction</h3>
                                <hr />
                                <p>
                                    This privacy notice provides you with details of how we collect and process your
                                    personal data through your use of our <a href="https://www.ag-grid.com/">site. </a>
                                    By providing us with your data, you warrant to us that you are over 13 years of age.
                                    AG Grid Ltd. is the data controller and we are responsible for your personal data
                                    (referred to as “we”, “us” or “our” in this privacy notice).
                                </p>
                                <h4>
                                    <strong>Contact Details</strong>
                                </h4>
                                <p>Our full details are: </p>
                                <p>
                                    Full name of legal entity: <strong>AG Grid Ltd.</strong>
                                    <br />
                                    Email address:{' '}
                                    <a href="mailto:info@ag-grid.com" target="_top">
                                        info@ag-grid.com
                                    </a>
                                    <br />
                                    Postal address:{' '}
                                    <strong>
                                        Bank Chambers, 6 Borough High Street, London, SE1 9QQ, United Kingdom.
                                    </strong>
                                    <br />
                                </p>
                                <p>
                                    It is very important that the information we hold about you is accurate and up to
                                    date. Please let us know if at any time your personal information changes by
                                    emailing us at{' '}
                                    <a href="mailto:info@ag-grid.com" target="_top">
                                        info@ag-grid.com
                                    </a>
                                </p>
                            </li>
                            <li>
                                <h3 id="data-what-why">
                                    What Data Do We Collect About You, For What Purpose And On What Ground We Process It
                                </h3>
                                <hr />
                                <h4>
                                    Personal data means any information capable of identifying an individual. It does
                                    not include anonymised data.
                                </h4>
                                <h4>We may process the following categories of personal data about you:</h4>
                                <p>
                                    <strong>Communication Data</strong> that includes any communication that you send to
                                    us whether that be through the contact form on our website, through email, text,
                                    social media messaging, social media posting or any other communication that you
                                    send us. We process this data for the purposes of communicating with you, for record
                                    keeping and for the establishment, pursuance or defence of legal claims. Our lawful
                                    ground for this processing is our legitimate interests which in this case are to
                                    reply to communications sent to us, to keep records and to establish, pursue or
                                    defend legal claims.
                                </p>
                                <p>
                                    <strong>Customer Data</strong> that includes data relating to any purchases of goods
                                    and/or services such as your name, title, billing address, delivery address email
                                    address, phone number, contact details, purchase details and your card details. We
                                    process this data to supply the goods and/or services you have purchased and to keep
                                    records of such transactions. Our lawful ground for this processing is the
                                    performance of a contract between you and us and/or taking steps at your request to
                                    enter into such a contract.
                                </p>
                                <p>
                                    <strong>User Data</strong> that includes data about how you use our website and any
                                    online services together with any data that you post for publication on our website
                                    or through other online services. We process this data to operate our website and
                                    ensure relevant content is provided to you, to ensure the security of our website,
                                    to maintain back- ups of our website and/or databases and to enable publication and
                                    administration of our website, other online services and business. Our lawful ground
                                    for this processing is our legitimate interests which in this case are to enable us
                                    to properly administer our website and our business.
                                </p>
                                <p>
                                    <strong>Technical Data</strong> that includes data about your use of our website and
                                    online services such as your IP address, your login data, details about your
                                    browser, length of visit to pages on our website, page views and navigation paths,
                                    details about the number of times you use our website, time zone settings and other
                                    technology on the devices you use to access our website. The source of this data is
                                    from our analytics tracking system. We process this data to analyse your use of our
                                    website and other online services, to administer and protect our business and
                                    website, to deliver relevant website content and advertisements to you and to
                                    understand the effectiveness of our advertising. Our lawful ground for this
                                    processing where essential cookies are used (those which are required to enable the
                                    website to function) is our legitimate interests which in this case are to enable us
                                    to properly administer our website and our business and to grow our business and to
                                    decide our marketing strategy. Where non-essential cookies, such as analytics or
                                    advertising cookies are used, we will rely on your consent obtained through the
                                    cookie banner to process your personal data.
                                </p>
                                <p>
                                    <strong>Marketing Data</strong> that includes data about your preferences in
                                    receiving marketing from us and our third parties and your communication
                                    preferences. We process this data to enable you to partake in our promotions such as
                                    competitions, prize draws and free give-aways, to deliver relevant website content
                                    and advertisements to you and measure or understand the effectiveness of this
                                    advertising. Our lawful ground for this processing is our consent, where we have
                                    asked for it or legitimate interests, where you have previously purchased from us or
                                    expressed an interest in our services or products, which in this case are to study
                                    how customers use our products/services, to develop them, to grow our business and
                                    to decide our marketing strategy.
                                </p>
                                <p>
                                    We may use Customer Data, User Data, Technical Data and Marketing Data to deliver
                                    relevant website content and advertisements to you (including Facebook adverts or
                                    other display advertisements) and to measure or understand the effectiveness of the
                                    advertising we serve you. Our lawful ground for this processing your consent. We may
                                    also use such data to send other marketing communications to you. Our lawful ground
                                    for this processing is either consent or legitimate interests (namely to grow our
                                    business). <strong>[NOTE SEE SECTION 4 BELOW]</strong>
                                </p>
                                <p>
                                    <strong>Sensitive Data</strong> We do not collect any Sensitive Data about you.
                                    Sensitive data refers to data that includes details about your race or ethnicity,
                                    religious or philosophical beliefs, sex life, sexual orientation, political
                                    opinions, trade union membership, information about your health and genetic and
                                    biometric data. We do not collect any information about criminal convictions and
                                    offences.
                                </p>
                                <p>
                                    Where we are required to collect personal data by law, or under the terms of the
                                    contract between us and you do not provide us with that data when requested, we may
                                    not be able to perform the contract (for example, to deliver goods or services to
                                    you). If you don’t provide us with the requested data, we may have to cancel a
                                    product or service you have ordered but if we do, we will notify you at the time.
                                </p>
                                <p>
                                    We will only use your personal data for a purpose it was collected for or a
                                    reasonably compatible purpose if necessary. For more information on this please
                                    email us at{' '}
                                    <a href="mailto:info@ag-grid.com" target="_top">
                                        info@ag-grid.com
                                    </a>
                                    . In case we need to use your details for an unrelated new purpose we will let you
                                    know and explain the legal grounds for processing.
                                </p>
                                <p>
                                    We may process your personal data without your knowledge or consent where this is
                                    required or permitted by law.
                                </p>
                                <p>We do not carry out automated decision making or any type of automated profiling.</p>
                            </li>
                            <li>
                                <h3 id="data-how-we-collect">How We Collect Your Personal Data</h3>
                                <hr />
                                <p>
                                    We may collect data about you by you providing the data directly to us (for example
                                    by filling in forms on our site or by sending us emails). We may automatically
                                    collect certain data from you as you use our website by using cookies and similar
                                    technologies. Please see our <a href="/cookies">Cookie Policy</a> for more details
                                    about this.
                                </p>
                                <p>
                                    We may receive data from third parties such as analytics providers such as Google
                                    based outside the EU, advertising networks such as Facebook based outside the EU,
                                    such as search information providers such as Google based outside the EU, providers
                                    of technical, payment and delivery services, such as data brokers or aggregators.
                                </p>
                                <p>
                                    We may also receive data from publicly availably sources such as Companies House and
                                    the Electoral Register based inside the EU.
                                </p>
                            </li>
                            <li>
                                <h3 id="marketing-comms">Marketing Communications</h3>
                                <hr />
                                <p>
                                    Our lawful ground of processing your personal data to send you marketing
                                    communications is either your consent or our legitimate interests (namely to grow
                                    our business).
                                </p>
                                <p>
                                    Under the Privacy and Electronic Communications Regulations, we may send you
                                    marketing communications from us if:
                                </p>
                                <ul>
                                    <li>
                                        you made a purchase or asked for information from us about our goods or services
                                        or
                                    </li>
                                    <li>
                                        you agreed to receive marketing communications and in each case you have not
                                        opted out of receiving such communications since. Under these regulations, if
                                        you are a limited company, we may send you marketing emails without your
                                        consent. However you can still opt out of receiving marketing emails from us at
                                        any time.
                                    </li>
                                </ul>
                                <p>
                                    Before we share your personal data with any third party for their own marketing
                                    purposes we will get your express consent.
                                </p>
                                <p>
                                    You can ask us or third parties to stop sending you marketing messages at any time
                                    by following the opt-out links on any marketing message sent to you or by emailing
                                    us at{' '}
                                    <a href="mailto:info@ag-grid.com" target="_top">
                                        info@ag-grid.com
                                    </a>{' '}
                                    at any time.
                                </p>
                                <p>
                                    If you opt out of receiving marketing communications this opt-out does not apply to
                                    personal data provided as a result of other transactions, such as purchases,
                                    warranty registrations etc.
                                </p>
                            </li>
                            <li>
                                <h3 id="data-disclosure">Disclosures Of Your Personal Data</h3>
                                <hr />
                                <h4>We may have to share your personal data with the parties set out below:</h4>
                                <ul>
                                    <li>Service providers who provide IT and system administration services.</li>
                                    <li>Professional advisers including lawyers, bankers, auditors and insurers</li>
                                    <li>Government bodies that require us to report processing activities.</li>
                                    <li>
                                        Third parties to whom we sell, transfer, or merge parts of our business or our
                                        assets.
                                        <p>
                                            We require all third parties to whom we transfer your data to respect the
                                            security of your personal data and to treat it in accordance with the law.
                                            We only allow such third parties to process your personal data for specified
                                            purposes and in accordance with our instructions.
                                        </p>
                                    </li>
                                </ul>
                            </li>
                            <li>
                                <h3 id="intl-transfers">International Transfers</h3>
                                <hr />
                                <p>
                                    Countries outside of the European Economic Area <strong>(EEA)</strong>do not always
                                    offer the same levels of protection to your personal data, so European law has
                                    prohibited transfers of personal data outside of the EEA unless the transfer meets
                                    certain criteria.
                                </p>
                                <p>
                                    Many of our third party service providers are based outside the European Economic
                                    Area <strong>(EEA)</strong> so their processing of your personal data will involve a
                                    transfer of data outside the EEA.
                                </p>
                                <p>
                                    Whenever we transfer your personal data out of the EEA, we do our best to ensure a
                                    similar degree of security of data by ensuring one of the following safeguards is in
                                    place:
                                </p>
                                <ul>
                                    <li>
                                        We will only transfer your personal data to countries that the European
                                        Commission have approved as providing an adequate level of protection for
                                        personal data by; or
                                    </li>
                                    <li>
                                        Where we use certain service providers, we may use specific contracts or codes
                                        of conduct or certification mechanisms approved by the European Commission which
                                        give personal data the same protection it has in Europe; or
                                    </li>
                                </ul>
                            </li>
                            <li>
                                <h3 id="data-security">Data Security</h3>
                                <hr />
                                <p>
                                    We have put in place security measures to prevent your personal data from being
                                    accidentally lost, used, altered, disclosed, or accessed without authorisation. We
                                    also allow access to your personal data only to those employees and partners who
                                    have a business need to know such data. They will only process your personal data on
                                    our instructions and they must keep it confidential.
                                </p>
                                <p>
                                    We have procedures in place to deal with any suspected personal data breach and will
                                    notify you and any applicable regulator of a breach if we are legally required to.
                                </p>
                            </li>
                            <li>
                                <h3 id="data-retention">Data Retention</h3>
                                <hr />
                                <p>
                                    We will only retain your personal data for as long as necessary to fulfil the
                                    purposes we collected it for, including for the purposes of satisfying any legal,
                                    accounting, or reporting requirements.
                                </p>
                                <p>
                                    When deciding what the correct time is to keep the data for we look at its amount,
                                    nature and sensitivity, potential risk of harm from unauthorised use or disclosure,
                                    the processing purposes, if these can be achieved by other means and legal
                                    requirements.
                                </p>
                                <p>
                                    For tax purposes the law requires us to keep basic information about our customers
                                    (including Contact, Identity, Financial and Transaction Data) for six years after
                                    they stop being customers.
                                </p>
                                <p>
                                    In some circumstances we may anonymise your personal data for research or
                                    statistical purposes in which case we may use this information indefinitely without
                                    further notice to you.
                                </p>
                            </li>
                            <li>
                                <h3 id="legal-rights">Your Legal Rights</h3>
                                <hr />
                                <p>
                                    Under data protection laws you have rights in relation to your personal data that
                                    include the right to request access, correction, erasure, restriction, transfer, to
                                    object to processing, to portability of data and (where the lawful ground of
                                    processing is consent) to withdraw consent.
                                </p>
                                <p>
                                    You can see more about these rights at:{' '}
                                    <a href="https://ico.org.uk/for-organisations/guide-to-the-general-data-protection-regulation-gdpr/individual-rights/">
                                        {' '}
                                        https://ico.org.uk/for-organisations/guide-to-the-general-data-protection-regulation-gdpr/individual-rights/
                                    </a>
                                </p>
                                <p>
                                    If you wish to exercise any of the rights set out above, please email us at{' '}
                                    <a href="mailto:info@ag-grid.com" target="_top">
                                        info@ag-grid.com
                                    </a>
                                    .
                                </p>
                                <p>
                                    You will not have to pay a fee to access your personal data (or to exercise any of
                                    the other rights). However, we may charge a reasonable fee if your request is
                                    clearly unfounded, repetitive or excessive or refuse to comply with your request in
                                    these circumstances.
                                </p>
                                <p>
                                    We may need to request specific information from you to help us confirm your
                                    identity and ensure your right to access your personal data (or to exercise any of
                                    your other rights). This is a security measure to ensure that personal data is not
                                    disclosed to any person who has no right to receive it. We may also contact you to
                                    ask you for further information in relation to your request to speed up our
                                    response.
                                </p>
                                <p>
                                    We try to respond to all legitimate requests within one month. Occasionally it may
                                    take us longer than a month if your request is particularly complex or you have made
                                    a number of requests. In this case, we will notify you.
                                </p>
                                <p>
                                    If you are not happy with any aspect of how we collect and use your data, you have
                                    the right to complain to the Information Commissioner’s Office (ICO), the UK
                                    supervisory authority for data protection issues{' '}
                                    <a href="https://www.ico.org.uk">(www.ico.org.uk)</a>. We should be grateful if you
                                    would contact us first if you do have a complaint so that we can try to resolve it
                                    for you.
                                </p>
                            </li>
                            <li>
                                <h3 id="third-party-links">Third-Party Links</h3>
                                <hr />
                                <p>
                                    This website may include links to third-party websites, plug-ins and applications.
                                    Clicking on those links or enabling those connections may allow third parties to
                                    collect or share data about you. We do not control these third-party websites and
                                    are not responsible for their privacy statements. When you leave our website, we
                                    encourage you to read the privacy notice of every website you visit.
                                </p>
                            </li>
                            <li>
                                <h3 id="cookies-privacy">Cookies</h3>
                                <hr />
                                <p>
                                    You can set your browser to refuse all or some browser cookies, or to alert you when
                                    websites set or access cookies. If you disable or refuse cookies, please note that
                                    some parts of this website may become inaccessible or not function properly. For
                                    more information about the cookies we use, please see:{' '}
                                    <a href="/cookies">Our Cookies Policy</a>.
                                </p>
                            </li>
                        </ol>
                    </section>
                </div>
            </div>
        </>
    );
};

export default Privacy;
